import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@cotera/client/v0/lib/utils';
import { resourceColorBadgeClassName } from '@cotera/client/app/v1/resources/type/resource-type-colors';

const sizeVariants = {
  default: 'px-2 py-1 text-xs',
  sm: 'px-1 py-0.5 text-xs',
  lg: 'px-2.5 py-1.5 text-sm',
};

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border border-border px-2.5 py-1 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1.5 [&>svg]:pointer-events-none [&>svg]:shrink-0 leading-normal transition-colors',
  {
    variants: {
      variant: {
        // Color-based variants
        blue: 'border-blue-200 bg-blue-50 text-blue-600 [&>svg]:text-blue-600',
        amber:
          'border-amber-200 bg-amber-50 text-amber-600 [&>svg]:text-amber-600',
        purple:
          'border-mention-purple-border bg-mention-purple-bg text-mention-purple [&>svg]:text-mention-purple',
        primary:
          'border-primary/30 bg-primary/10 text-primary [&>svg]:text-primary',
        gray: 'border-gray-200 bg-gray-50 text-gray-700 [&>svg]:text-gray-700',
        orange:
          'border-orange-200 bg-orange-50 text-orange-600 [&>svg]:text-orange-600',
        rose: 'border-rose-200 bg-rose-50 text-rose-600 [&>svg]:text-rose-600',
        green:
          'border-emerald-200 bg-emerald-50 text-emerald-600 [&>svg]:text-emerald-600',
        slate:
          'border-slate-200 bg-slate-50 text-slate-600 [&>svg]:text-slate-600',
        violet:
          'border-violet-200 bg-violet-50 text-violet-600 [&>svg]:text-violet-600',
        teal: 'border-teal-200 bg-teal-50 text-teal-600 [&>svg]:text-teal-600',
        brown: resourceColorBadgeClassName('tool'),
        muted: 'border-gray-200 bg-gray-50 text-gray-700 [&>svg]:text-gray-700',
        // Legacy variants for backward compatibility
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-destructive/60 bg-destructive/10 text-destructive/80 [a&]:hover:bg-destructive/30 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        yellow:
          'border-yellow-500/60 bg-yellow-500/10 text-yellow-500 [a&]:hover:bg-yellow-500/30 focus-visible:ring-yellow-500/20',
        cotera:
          'border-indigo-500/60 bg-indigo-500/10 text-indigo-500 [a&]:hover:bg-indigo-500/20',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  size = 'default',
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    size?: 'default' | 'sm' | 'lg';
  }) {
  const Comp = asChild ? Slot.Root : 'span';

  const sizeClasses = sizeVariants[size];
  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), sizeClasses, className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
